import type { ApiUser } from '#/api/userEnvironment/types/ApiUserEnvironment';
import { Button } from '#/components/Button/Button';
import { Dropdown } from '#/components/Dropdown/Dropdown';
import { DropdownListMenu } from '#/components/Dropdown/menus/ListMenu/DropdownListMenu';
import {
  DropdownListMenuSection,
  DropdownListMenuSectionTitle,
} from '#/components/Dropdown/menus/ListMenu/DropdownListMenuSection';
import { Icon } from '#/components/Icon/Icon';
import { useI18n } from '#/i18n/hooks/useI18n';
import { LayoutHeaderButton } from '#/layouts/Layout/Header/Button/LayoutHeaderButton';
import { orgDashboardManageListingsRoute } from '#/modules/orgDashboard/routing/orgDashboard.routes';
import { userDashboardAccountSettingsRoute } from '#/modules/userDashboard/routing/userDashboard.routes';

import {
  WorkspaceDropdownButtonContent,
  WorkspaceMobileDropdownBody,
  WorkspaceMobileDropdownHeader,
  WorkspaceMobileDropdownHolder,
  WorkspaceMobileDropdownLink,
  WorkspaceMobileDropdownSection,
  WorkspaceMobileDropdownSectionTitle,
  WorkspaceDropdownListMenuItem,
} from './WorkspaceDropdown.styled';

type Props = {
  user: ApiUser;
};

export function WorkspaceDropdown({ user }: Props) {
  const { i18n } = useI18n();

  return (
    <Dropdown
      trigger={
        <LayoutHeaderButton
          data={{ type: 'inert' }}
          variant="secondaryGrey"
          qaId="workspace-dropdown-toggle"
          ariaLabel={i18n('Workspace Selection')}
        >
          <WorkspaceDropdownButtonContent>
            <Icon name="building" size={16} />
            <div className="workspace-text">{i18n('Workspaces')}</div>
            <Icon name="chevron-updown" size={16} />
          </WorkspaceDropdownButtonContent>
        </LayoutHeaderButton>
      }
      trackClickedOpen={{ name: 'Header Utility CTA - Workspaces' }}
    >
      <DropdownListMenu
        width={240}
        maxHeight="calc(100vh - var(--root-padding-top) - 20px)"
        qaId="workspace-dropdown"
      >
        {user.orgs.length > 0 && (
          <DropdownListMenuSection>
            <DropdownListMenuSectionTitle>
              {i18n('Workspaces')}
            </DropdownListMenuSectionTitle>

            {user.orgs.map((org) => (
              <WorkspaceDropdownListMenuItem
                key={org.id}
                type="link"
                href={orgDashboardManageListingsRoute.with({ orgId: org.id })}
                clickActions={{
                  closeRichDetailsFromWithin: true,
                  track: {
                    name: 'Header Dropdown Link',
                    properties: { link_type: 'Organization' },
                  },
                }}
              >
                <Icon name="building" size={16} />
                <span className="truncate">{org.name}</span>
              </WorkspaceDropdownListMenuItem>
            ))}

            <WorkspaceDropdownListMenuItem
              key="personal"
              qaId="personal-dashboard-link"
              type="link"
              href={userDashboardAccountSettingsRoute.with({})}
              clickActions={{
                closeRichDetailsFromWithin: true,
                track: {
                  name: 'Workspace Dropdown Link',
                  properties: { link_type: 'Organization' },
                },
              }}
            >
              <Icon name="person" size={16} />
              <span className="truncate">{i18n('Personal Dashboard')}</span>
            </WorkspaceDropdownListMenuItem>
          </DropdownListMenuSection>
        )}
      </DropdownListMenu>

      <WorkspaceMobileDropdownHolder>
        <WorkspaceMobileDropdownHeader>
          <Button
            type="button"
            aria-label={i18n('Close menu')}
            qaId="workspace-dropdown-close-mobile"
            variant="tertiary-grey"
            icon={{ name: 'close' }}
            clickActions={{
              closeRichDetailsFromWithin: true,
            }}
          />
        </WorkspaceMobileDropdownHeader>

        <WorkspaceMobileDropdownBody data-qa-id="workspace-dropdown-mobile">
          {user.orgs.length > 0 && (
            <WorkspaceMobileDropdownSection>
              <WorkspaceMobileDropdownSectionTitle>
                {i18n('Workspaces')}
              </WorkspaceMobileDropdownSectionTitle>

              {user.orgs.map((org) => (
                <div key={org.id}>
                  <WorkspaceMobileDropdownLink
                    type="link"
                    href={`/admin/${org.id}`}
                    clickActions={{
                      closeRichDetailsFromWithin: true,
                    }}
                  >
                    <Icon name="building" size={16} />
                    <span data-qa-id="mobile-dashboard-link">{org.name}</span>
                  </WorkspaceMobileDropdownLink>
                </div>
              ))}
              <div key="personal">
                <WorkspaceMobileDropdownLink
                  type="link"
                  href={userDashboardAccountSettingsRoute.with({})}
                  qaId="personal-dashboard-link"
                  clickActions={{
                    closeRichDetailsFromWithin: true,
                  }}
                >
                  <Icon name="person" size={16} />
                  <span data-qa-id="mobile-dashboard-link">
                    {i18n('Personal Dashboard')}
                  </span>
                </WorkspaceMobileDropdownLink>
              </div>
            </WorkspaceMobileDropdownSection>
          )}
        </WorkspaceMobileDropdownBody>
      </WorkspaceMobileDropdownHolder>
    </Dropdown>
  );
}
